import React, { createContext, useContext, useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import { API_AUTH_TYPES, API_ROUTES, ApiRequest } from '../../../../api';
import { useAuth } from '../../../../context/auth-context';

const ParabotContext = createContext(null);
const parabotApi = new ApiRequest(API_ROUTES.PARABOT, API_AUTH_TYPES.ADVISOR);
const CONVERSATIONS_PAGE_SIZE = 20;

export const ParabotProviderV2 = ({ children }) => {
  const { isAdvisor, accessToken } = useAuth();
  const {
    data: conversationData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryFn: ({ pageParam = 0 }) =>
      parabotApi.get({
        endpoint: 'index_conversation_ids',
        queryParams: { limit: CONVERSATIONS_PAGE_SIZE, offset: pageParam },
      }),
    queryKey: 'parabot_conversation_ids',
    enabled: isAdvisor && Boolean(accessToken),
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextOffset : undefined),
    refetchInterval: (latest) =>
      latest?.pages?.some((page) =>
        page?.conversations?.some((conversation) => conversation.isRunning)
      )
        ? 5000
        : false,
  });

  const value = useMemo(() => {
    // New conversations shift offsets between page fetches, so dedupe by id
    // when flattening the loaded pages.
    const seenIds = new Set();
    const conversations = [];
    (conversationData?.pages || []).forEach((page) => {
      (page?.conversations || []).forEach((conversation) => {
        if (seenIds.has(conversation.id)) return;
        seenIds.add(conversation.id);
        conversations.push(conversation);
      });
    });

    return {
      conversations,
      // unreadCount is global (not per page), so any page's value works.
      unreadCount: conversationData?.pages?.[0]?.unreadCount ?? 0,
      fetchMoreConversations: fetchNextPage,
      hasMoreConversations: Boolean(hasNextPage),
      isFetchingMoreConversations: Boolean(isFetchingNextPage),
    };
  }, [conversationData, fetchNextPage, hasNextPage, isFetchingNextPage]);

  return <ParabotContext.Provider value={value}>{children}</ParabotContext.Provider>;
};

export const useParabotV2 = () => {
  const context = useContext(ParabotContext);
  if (!context) {
    throw new Error('useParabotV2 must be used within a ParabotProviderV2');
  }
  return context;
};

export default ParabotContext;
